import {
  Centered,
  ContentContainer,
  FlexColumn,
  FlexRow,
  Loading,
  MaterialIcon,
  StyledButton,
  useLoading,
  useModal,
} from '@gorila-shared-ui/components';
import { Heading, HeadingLevel } from 'baseui/heading';
import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { JOBS_TYPES_PAGE } from '../../constants/app';
import { useFeedback } from '../../hooks/useFeedback';
import { useRouting } from '../../hooks/useRouting';
import { useStyles } from '../../hooks/useStyles';
import { getJobType } from '../../services/jobTypesService';
import { selectedJobActivityIdState, updateJobTypeState } from '../../storage/jobTypesStates';
import { JobType } from '../../types/jobs';
import { JobActivities } from '../jobs/activities/JobActivities';
import { JobTypeModal } from '../jobs/types/JobTypeModal';
import { JobTypeResume } from '../jobs/types/JobTypeResume';

export function JobTypeDetailPage() {
  const { css } = useStyles();
  const { jobTypeId } = useParams();
  const [jobTypeDetail, setJobTypeDetail] = useState<JobType>();
  const { goToPage } = useRouting();
  const { showFailFeedback } = useFeedback();
  const { loading, startLoading, stopLoading } = useLoading(true);
  const { openModal: openEditModal, closeModal: closeEditModal, showModal: showEditModal } = useModal();
  const setSelectedActivityId = useSetRecoilState(selectedJobActivityIdState);
  const [update, setUpdate] = useRecoilState(updateJobTypeState);

  const onUpdate = () => {
    loadJobTypeDetail();
  };

  useEffect(() => {
    if (!jobTypeId) return;
    loadJobTypeDetail();
  }, [jobTypeId]);

  useEffect(() => {
    if (!update) return;
    loadJobTypeDetail();
    setUpdate(undefined);
  }, [update]);

  const loadJobTypeDetail = async () => {
    if (!jobTypeId) return;
    startLoading();
    const { jobType, error } = await getJobType(jobTypeId);
    if (error) {
      goToPage(JOBS_TYPES_PAGE.route, true);
      showFailFeedback(error);
    } else if (jobType) {
      setJobTypeDetail(jobType);
    }
    stopLoading();
  };

  const onSubmitEditFinished = () => {
    onUpdate();
    closeEditModal();
  };

  if (!jobTypeId) {
    return (
      <Navigate
        to={JOBS_TYPES_PAGE.route}
        replace
      />
    );
  }

  if (!jobTypeDetail && loading) {
    return (
      <ContentContainer>
        <Centered>
          <Loading />
        </Centered>
      </ContentContainer>
    );
  }

  return (
    <ContentContainer
      classNames={css({
        padding: '0 16px 16px !important',
        gap: '0 !important',
        overflowY: 'auto',
      })}
    >
      {jobTypeDetail && !loading && (
        <FlexColumn classNames={css({ flex: 1 })}>
          <FlexRow classNames={css({ alignItems: 'center' })}>
            <HeadingLevel>
              <Heading styleLevel={5}>
                <u>Datos generales</u>
              </Heading>
            </HeadingLevel>
            <StyledButton
              shape="pill"
              size="mini"
              kind="secondary"
              onClick={openEditModal}
            >
              <FlexRow gap={'4px'}>
                <MaterialIcon
                  name="edit"
                  size="16px"
                />
                Editar
              </FlexRow>
            </StyledButton>
          </FlexRow>
          <JobTypeResume jobType={jobTypeDetail} />
          <FlexRow classNames={css({ alignItems: 'center' })}>
            <HeadingLevel>
              <Heading styleLevel={5}>
                <u>Actividades</u>
              </Heading>
            </HeadingLevel>
            <StyledButton
              shape="pill"
              size="mini"
              kind="secondary"
              onClick={() => {
                setSelectedActivityId('new');
              }}
            >
              <FlexRow gap={'4px'}>
                <MaterialIcon
                  name="add"
                  size="16px"
                />
                Nueva
              </FlexRow>
            </StyledButton>
          </FlexRow>
          <FlexRow classNames={css({ flex: 1 })}>
            <JobActivities />
          </FlexRow>
        </FlexColumn>
      )}
      <JobTypeModal
        isOpen={showEditModal}
        onClose={closeEditModal}
        onSubmitFinished={onSubmitEditFinished}
        jobType={jobTypeDetail}
        jobTypeId={jobTypeDetail?._id}
      />
    </ContentContainer>
  );
}
